import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStore } from '../../context/StoreContext';
import {
    Store, CheckCircle2, AlertTriangle, Loader2, Zap,
    Building2, MapPin, Power, Copy, X, KeyRound,
    ShieldCheck, Wand2, Edit3, UserCheck, PlusCircle, Hash,
} from 'lucide-react';
import styles from './StoreActivator.module.css';

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001').replace(/\/api\/v1(?:\/public)?\/?$/, '').replace(/\/+$/, '');
const STORE_API = `${API_ORIGIN}/api/v1/smiify/sm/stores`;
const APP_URL = 'https://app.hirsta.com';

const codeChip = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, background: 'var(--h-color-primary-50, #f5f6ff)', color: 'var(--h-color-primary-700, #471ea7)', fontSize: 11, fontWeight: 900, letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums' };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

const StoreActivator = () => {
    const { user } = useAuth();
    const { activeStore } = useStore();
    const navigate = useNavigate();

    const [stores, setStores] = useState([]);
    const [partnerInfo, setPartnerInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const [fetchingCredsId, setFetchingCredsId] = useState(null);
    const [generatedCreds, setGeneratedCreds] = useState(null);

    const [configStore, setConfigStore] = useState(null);
    const [isActivating, setIsActivating] = useState(false);
    const [isManualMode, setIsManualMode] = useState(false);
    const [formData, setFormData] = useState({ displayName: '', username: '', password: '', role: 'ADMIN' });

    const isHQActive = activeStore?.isHQ || activeStore?.id === 'global_hq' || activeStore?._id === 'global_hq' || activeStore?.type === 'HQ';

    // only the owner, at HQ (waits until both are loaded, so it never redirects too early)
    useEffect(() => {
        if (user && activeStore && (user.role !== 'SUPERADMIN' || !isHQActive)) navigate('/', { replace: true });
    }, [user, activeStore, isHQActive, navigate]);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
    };

    const fetchStores = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('smiify_token');
            const response = await fetch(STORE_API, { headers: { Authorization: `Bearer ${token}` } });
            const result = await response.json();
            if (result.success) {
                setStores(result.data || []);
                setPartnerInfo(result.partner || null);
            } else showToast(result.message || 'Could not load branches.', 'error');
        } catch (error) {
            showToast('Network error while loading branches.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === 'SUPERADMIN' && isHQActive) fetchStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.role, isHQActive]);

    const isSmiifyActive = (store) => ['ACTIVE', 'TRIAL'].includes(store?.smiifySubscription?.status);

    const openConfigModal = (store) => {
        setConfigStore(store);
        setIsManualMode(false);
        setFormData({ displayName: '', username: '', password: '', role: 'ADMIN' });
    };

    const handleFetchCredentials = async (store) => {
        setFetchingCredsId(store._id);
        try {
            const token = localStorage.getItem('smiify_token');
            const response = await fetch(`${STORE_API}/${store._id}/credentials`, { headers: { Authorization: `Bearer ${token}` } });
            const result = await response.json();
            if (response.ok && result.success) {
                setGeneratedCreds({ storeName: store.storeName, storeCode: store.smiifyCode, ...result.credentials });
            } else {
                showToast('Admin login not found for this branch. Create it now.', 'error');
                openConfigModal(store);
            }
        } catch (error) {
            showToast('Network error.', 'error');
        } finally {
            setFetchingCredsId(null);
        }
    };

    const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const executeActivation = async () => {
        if (isManualMode && (!formData.username || !formData.password || !formData.displayName)) {
            showToast('Fill name, username and password for a manual setup.', 'error');
            return;
        }
        if (isManualMode && formData.password.length < 8) {
            showToast('Password must be at least 8 characters.', 'error');
            return;
        }
        setIsActivating(true);
        try {
            const token = localStorage.getItem('smiify_token');
            const response = await fetch(`${STORE_API}/${configStore._id}/activate`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ isManual: isManualMode, ...formData }),
            });
            const result = await response.json();
            if (response.ok && result.success) {
                const code = result.store?.smiifyCode || result.credentials?.storeCode || '';
                setConfigStore(null);
                if (result.credentials) setGeneratedCreds({ storeName: configStore.storeName, storeCode: code, ...result.credentials });
                setStores((prev) => prev.map((s) => (s._id === configStore._id ? { ...s, smiifyCode: code || s.smiifyCode, smiifySubscription: { ...s.smiifySubscription, status: 'ACTIVE' } } : s)));
                if (result.partner) setPartnerInfo((p) => ({ ...(p || {}), hirsta: { ...((p && p.hirsta) || {}), activeStores: result.partner.activeStores, status: result.partner.status } }));
                showToast(`${configStore.storeName} is active. Store code ${code}.`);
            } else {
                showToast(result.message || 'Could not activate the branch.', 'error');
            }
        } catch (error) {
            showToast('Network error during activation.', 'error');
        } finally {
            setIsActivating(false);
        }
    };

    const copyToClipboard = () => {
        if (!generatedCreds) return;
        const text = [
            `🏢 Store: ${generatedCreds.storeName}`,
            generatedCreds.storeCode ? `#️⃣ Store code: ${generatedCreds.storeCode}` : null,
            `👤 Name: ${generatedCreds.displayName}`,
            `🛡️ Role: ${generatedCreds.role}`,
            `🔗 Login: ${APP_URL}`,
            '',
            `Username: ${generatedCreds.username}`,
            `Password: ${generatedCreds.password}`,
        ].filter((x) => x !== null).join('\n');
        navigator.clipboard.writeText(text);
        showToast('Login details copied.');
    };

    if (!activeStore || user?.role !== 'SUPERADMIN' || !isHQActive) return null;

    const hirsta = partnerInfo?.hirsta || {};
    const activeCount = stores.filter(isSmiifyActive).length;

    return (
        <div className={styles.pageContainer}>
            {toast.show && (
                <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>
                    {toast.type === 'error' ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
                    <span>{toast.message}</span>
                </div>
            )}

            {/* ACTIVATION / LOGIN SETUP */}
            {configStore && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalTitleWrap}>
                                <ShieldCheck size={22} className={styles.primaryIcon} />
                                <div>
                                    <h2>{isSmiifyActive(configStore) ? 'Set up branch login' : 'Activate branch on Hirsta'}</h2>
                                    <span>{configStore.storeName}{configStore.smiifyCode ? ` · #${configStore.smiifyCode}` : ' · a store code is given on activation'}</span>
                                </div>
                            </div>
                            <button type="button" className={styles.closeBtn} onClick={() => setConfigStore(null)} aria-label="Close"><X size={18} /></button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.tabContainer}>
                                <button type="button" className={`${styles.tabBtn} ${!isManualMode ? styles.activeTab : ''}`} onClick={() => setIsManualMode(false)}>
                                    <Wand2 size={14} /> Auto generate
                                </button>
                                <button type="button" className={`${styles.tabBtn} ${isManualMode ? styles.activeTab : ''}`} onClick={() => setIsManualMode(true)}>
                                    <Edit3 size={14} /> Manual setup
                                </button>
                            </div>

                            {!isManualMode ? (
                                <div className={styles.autoSetupState}>
                                    <Wand2 size={32} className={styles.watermarkIcon} />
                                    <h4>Login made for you</h4>
                                    <p>Hirsta creates a username and a temporary password for the branch admin. You can copy and share them on the next screen.</p>
                                </div>
                            ) : (
                                <div className={styles.manualForm}>
                                    <div className={styles.inputGroup}>
                                        <label htmlFor="sa-name">Display name</label>
                                        <input id="sa-name" type="text" name="displayName" value={formData.displayName} onChange={handleFormChange} placeholder="e.g. Rahul Sharma" />
                                    </div>
                                    <div className={styles.formRow}>
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="sa-user">Username</label>
                                            <input id="sa-user" type="text" name="username" value={formData.username} onChange={handleFormChange} placeholder="branch_admin" autoComplete="off" />
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label htmlFor="sa-role">Role</label>
                                            <select id="sa-role" name="role" value={formData.role} onChange={handleFormChange}>
                                                <option value="ADMIN">Admin</option>
                                                <option value="MANAGER">Manager</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label htmlFor="sa-pass">Password (8+ characters)</label>
                                        <input id="sa-pass" type="text" name="password" value={formData.password} onChange={handleFormChange} placeholder="Enter a strong password" autoComplete="new-password" />
                                    </div>
                                </div>
                            )}

                            <div className={styles.modalActions}>
                                <button type="button" className={styles.cancelBtn} onClick={() => setConfigStore(null)}>Cancel</button>
                                <button type="button" className={styles.confirmBtn} onClick={executeActivation} disabled={isActivating}>
                                    {isActivating ? <Loader2 size={16} className={styles.spinnerWhite} /> : (isSmiifyActive(configStore) ? <PlusCircle size={16} /> : <Power size={16} />)}
                                    {isActivating ? 'Working...' : (isSmiifyActive(configStore) ? 'Create login' : 'Activate branch')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* LOGIN DETAILS */}
            {generatedCreds && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalTitleWrap}>
                                <UserCheck size={22} className={styles.successIcon} />
                                <div>
                                    <h2>Branch login details</h2>
                                    <span>Share these with the branch manager</span>
                                </div>
                            </div>
                            <button type="button" className={styles.closeBtn} onClick={() => setGeneratedCreds(null)} aria-label="Close"><X size={18} /></button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.credBox}>
                                <div className={styles.credRow}><span className={styles.credLabel}>Store</span><span className={styles.credValue}>{generatedCreds.storeName}</span></div>
                                {generatedCreds.storeCode && <div className={styles.credRow}><span className={styles.credLabel}>Store code</span><span className={styles.credValueHighlight}>#{generatedCreds.storeCode}</span></div>}
                                <div className={styles.credRow}><span className={styles.credLabel}>User</span><span className={styles.credValue}>{generatedCreds.displayName} ({generatedCreds.role})</span></div>
                                <div className={styles.divider} />
                                <div className={styles.credRow}><span className={styles.credLabel}>Login ID</span><span className={styles.credValueHighlight}>{generatedCreds.username}</span></div>
                                <div className={styles.credRow}><span className={styles.credLabel}>Password</span><span className={styles.credValueHighlight}>{generatedCreds.password}</span></div>
                                <div className={styles.credRow}><span className={styles.credLabel}>Login at</span><span className={styles.credValue}>{APP_URL}</span></div>
                            </div>
                            <button type="button" className={styles.copyBigBtn} onClick={copyToClipboard}>
                                <Copy size={16} /> Copy login details
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <header className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.iconWrap}><Zap size={18} strokeWidth={2.5} /></div>
                    <div className={styles.headerCopy}>
                        <h1>Branch activation</h1>
                        <span className={styles.subtitle}>
                            {activeCount} of {stores.length} branches active on Hirsta
                            {hirsta.activatedAt ? ` · account live since ${fmtDate(hirsta.activatedAt)}` : ''}
                        </span>
                    </div>
                </div>
                <button type="button" className={styles.refreshBtn} onClick={fetchStores}>Refresh</button>
            </header>

            <main className={styles.workspace}>
                {isLoading ? (
                    <div className={styles.loadingState}>
                        <Loader2 size={26} className={styles.spinnerBrand} />
                        <span>Loading branches...</span>
                    </div>
                ) : stores.length === 0 ? (
                    <div className={styles.emptyState}>
                        <Building2 size={36} />
                        <p>No branches for this business yet.</p>
                    </div>
                ) : (
                    <div className={styles.storeGrid}>
                        {stores.map((store) => {
                            const isActivated = isSmiifyActive(store);
                            return (
                                <div key={store._id} className={styles.storeCard}>
                                    <div className={styles.cardHeader}>
                                        <div className={styles.storeNameWrap}>
                                            <Store size={16} color="var(--h-color-primary-600, #673de6)" />
                                            <h3>{store.storeName}</h3>
                                        </div>
                                        {isActivated ? <span className={styles.statusActive}>Active</span> : <span className={styles.statusPending}>Locked</span>}
                                    </div>
                                    <div className={styles.cardBody}>
                                        <div className={styles.infoRow}>
                                            <Hash size={13} color="#64748b" />
                                            {store.smiifyCode ? <span style={codeChip}>#{store.smiifyCode}</span> : <span className={styles.value}>Code given on activation</span>}
                                        </div>
                                        <div className={styles.infoRow}><MapPin size={13} color="#64748b" /><span>{store.city || 'Location not set'}</span></div>
                                        <div className={styles.infoRow}><span className={styles.label}>Tier:</span><span className={styles.value}>{store.salonTier || 'Standard'}</span></div>
                                        {store.smiifyActivatedAt && <div className={styles.infoRow}><span className={styles.label}>Active since:</span><span className={styles.value}>{fmtDate(store.smiifyActivatedAt)}</span></div>}
                                    </div>
                                    <div className={styles.cardFooter}>
                                        {isActivated ? (
                                            <button type="button" className={styles.viewCredsBtn} onClick={() => handleFetchCredentials(store)} disabled={fetchingCredsId === store._id}>
                                                {fetchingCredsId === store._id ? <Loader2 size={14} className={styles.spinnerBrand} /> : <KeyRound size={14} />}
                                                {fetchingCredsId === store._id ? 'Working...' : 'New password / set up login'}
                                            </button>
                                        ) : (
                                            <button type="button" className={styles.activateBtn} onClick={() => openConfigModal(store)}>
                                                <Power size={14} /> Activate branch
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
};

export default StoreActivator;
